import { Builder, builder } from '@builder.io/react';
import { getAsyncProps } from '@builder.io/utils';

import { I18nInstance } from '@catawiki/cata-style';

import {
  componentDataFetchers as anchorLinksComponentDataFetchers,
  NAME as anchorLinksComponentName,
  componentRegister as anchorLinksRegister,
} from '@/shared/components/AnchorLinks/AnchorLinks';
import {
  componentDataFetchers as auctionsComponentDataFetchers,
  NAME as auctionsComponentName,
  componentRegister as auctionsRegister,
} from '@/shared/components/Auctions/Auctions';
import {
  componentDataFetchers as allAuctionsComponentDataFetchers,
  componentRegister as allAuctionsRegister,
} from '@/shared/components/AllAuctions/AllAuctions';
import { componentRegister as popularAuctionsRegister } from '@/shared/components/PopularAuctions/PopularAuctions';
import {
  dataFetcher as categoryOverviewDataFetcher,
  componentRegister as categoryOverviewRegister,
} from '@/shared/components/CategoryOverview';
import {
  componentDataFetchers as pageNavigationComponentDataFetchers,
  componentRegister as pageNavigationRegister,
} from '@/shared/components/PageNavigation';
import {
  componentDataFetchers as collectionComponentDataFetchers,
  NAME as collectionComponentName,
  componentRegister as collectionsRegister,
} from '@/shared/components/Collections/Collections';
import {
  componentDataFetchers as contentImageComponentDataFetchers,
  NAME as contentImageComponentName,
  componentRegister as contentImageRegister,
} from '@/shared/components/ContentImage/ContentImage';
import {
  componentDataFetchers as checklistComponentDataFetchers,
  componentRegister as checklistRegister,
} from '@/shared/components/Checklist/Checklist';
import {
  componentDataFetchers as downloadAppsComponentDataFetchers,
  NAME as downloadAppsComponentName,
  componentRegister as downloadAppsRegister,
} from '@/shared/components/DownloadApps/DownloadApps';
import {
  componentDataFetchers as FAQComponentDataFetchers,
  NAME as FAQComponentName,
  componentRegister as FAQRegister,
} from '@/shared/components/FAQ/FAQ';
import {
  componentDataFetchers as heroComponentDataFetchers,
  NAME as heroComponentName,
  componentRegister as heroRegister,
} from '@/shared/components/Hero/Hero';
import {
  componentDataFetchers as highlightComponentDataFetchers,
  NAME as highlightComponentName,
  componentRegister as highlightRegister,
} from '@/shared/components/Highlight/Highlight';
import {
  componentDataFetchers as listExpertsComponentDataFetchers,
  NAME as listExpertsComponentName,
  componentRegister as listExpertsRegister,
} from '@/shared/components/ListExperts/ListExperts';
import {
  componentDataFetchers as lotsComponentDataFetchers,
  NAME as lotsComponentName,
  componentRegister as lotsRegister,
} from '@/shared/components/Lots/Lots';
import {
  componentDataFetchers as singleContentComponentDataFetchers,
  NAME as singleContentComponentName,
  componentRegister as singleContentRegister,
} from '@/shared/components/SingleContent/SingleContent';
import {
  componentDataFetchers as stepBoxComponentDataFetchers,
  componentRegister as stepBoxRegister,
} from '@/shared/components/StepBox';
import {
  componentDataFetchers as topicListComponentDataFetchers,
  NAME as topicListComponentName,
  componentRegister as topicListRegister,
} from '@/shared/components/TopicList/TopicList';
import {
  componentDataFetchers as videoTabsDataFetchers,
  componentRegister as videoTabsRegister,
} from '@/shared/components/VideoTabs/VideoTabs';
import {
  componentDataFetchers as duoContentsDataFetchers,
  componentRegister as duoContentsRegister,
} from '@/shared/components/DuoContents/DuoContents';
// Old deprecated components import
import {
  dataFetcher as expertsDataFetcher,
  componentRegister as expertsListRegister,
} from '@/shared/components/deprecated/ExpertsList';
import {
  componentRegister as selectedPartnerComponentRegister,
  dataFetcher as selectedPartnerDataFetcher,
} from '@/shared/components/deprecated/SelectedPartner';
import {
  componentRegister as articleComponentRegister,
  dataFetcher as articleDataFetcher,
} from '@/shared/components/deprecated/Article/Article';
import {
  dataFetcher as buttonDataFetcher,
  componentRegister as buttonRegister,
} from '@/shared/components/deprecated/Button';
import {
  componentRegister as dayAuctionComponentRegister,
  dataFetcher as dayAuctionDataFetcher,
} from '@/shared/components/deprecated/DayAuction/DayAuction';
import {
  dataFetcher as oldDownloadAppsDataFetcher,
  componentRegister as oldDownloadAppsRegister,
} from '@/shared/components/deprecated/DownloadApps';
import {
  componentRegister as exclusiveSelectionHeaderComponentRegister,
  dataFetcher as exclusiveSelectionHeaderDataFetcher,
} from '@/shared/components/deprecated/ExclusiveSelectionHeader/ExclusiveSelectionHeader';
import {
  componentRegister as howItWorksComponentRegister,
  dataFetcher as howItWorksDataFetcher,
} from '@/shared/components/deprecated/HowItWorks';
import {
  dataFetcher as oldLotsDataFetcher,
  componentRegister as oldLotsRegister,
} from '@/shared/components/deprecated/Lots';
// Components for testing plugins
import { componentRegister as devComponentRegister } from '@/shared/components/DevComponent';
import { componentRegister as testComponentRegister } from '@/shared/components/TestComponent';

import config from '../../config';
import { TRACER_NAME_BUILDER, getSpan } from './utils/tracer';

builder.init(config.builderApiKey);
export { builder };

export async function resolveBuilderContent(
  modelName: string,
  locale: string,
  pathPrefix: string,
  urlPath: string,
  i18n: I18nInstance
) {
  const span = getSpan(TRACER_NAME_BUILDER, 'builder.resolveBuilderContent');

  const builderApiSpan = getSpan(
    TRACER_NAME_BUILDER,
    'builder.loadBuilderContent'
  );
  const content = await builder
    .get(modelName, {
      userAttributes: {
        urlPath: `${pathPrefix}${urlPath || ''}`,
        locale,
      },
      query: {
        published: 'published',
      },
      options: { data: { locale }, locale },
    })
    .toPromise();
  builderApiSpan.end();

  await getAsyncProps(content, {
    ...anchorLinksComponentDataFetchers(locale),
    ...contentImageComponentDataFetchers(locale),
    ...heroComponentDataFetchers(locale),
    ...topicListComponentDataFetchers(locale),
    ...highlightComponentDataFetchers(locale),
    ...singleContentComponentDataFetchers(locale),
    ...stepBoxComponentDataFetchers(locale),
    ...pageNavigationComponentDataFetchers(locale),
    ...FAQComponentDataFetchers(locale),
    ...auctionsComponentDataFetchers(locale),
    ...allAuctionsComponentDataFetchers(locale),
    ...lotsComponentDataFetchers(locale, i18n),
    ...downloadAppsComponentDataFetchers(locale),
    ...collectionComponentDataFetchers(locale),
    ...listExpertsComponentDataFetchers(locale),
    ...videoTabsDataFetchers(locale),
    ...duoContentsDataFetchers(locale),
    ...checklistComponentDataFetchers(locale),

    // Deprecated components data fetchers
    Lots: oldLotsDataFetcher(locale, i18n),
    DownloadApps: oldDownloadAppsDataFetcher(locale),
    Article: articleDataFetcher(locale),
    Button: buttonDataFetcher(locale),
    DayAuction: dayAuctionDataFetcher(locale, i18n),
    CategoryOverview: categoryOverviewDataFetcher(locale),
    SelectedPartner: selectedPartnerDataFetcher(locale),
    HowItWorks: howItWorksDataFetcher(locale),
    ExclusiveSelectionHeader: exclusiveSelectionHeaderDataFetcher(locale),
    ExpertsList: expertsDataFetcher(locale),
  });

  span.end();
  return content || null;
}

export async function getAllPages(modelName: string) {
  const span = getSpan(TRACER_NAME_BUILDER, 'builder.getAllPages');
  const pages = await builder.getAll(modelName, {
    options: { noTargeting: true },
    query: {
      published: 'published',
    },
    omit: 'data.blocks',
  });
  span.end();
  return pages;
}

export function registerAllComponents() {
  const span = getSpan(TRACER_NAME_BUILDER, 'builder.registerAllComponents');
  const components: string[] = [
    heroRegister(),
    topicListRegister(),
    contentImageRegister(),
    highlightRegister(),
    singleContentRegister(),
    stepBoxRegister(),
    pageNavigationRegister(),
    duoContentsRegister(),
    FAQRegister(),
    auctionsRegister(),
    allAuctionsRegister(),
    popularAuctionsRegister(),
    lotsRegister(),
    downloadAppsRegister(),
    collectionsRegister(),
    anchorLinksRegister(),
    listExpertsRegister(),
    videoTabsRegister(),
    checklistRegister(),
  ];

  Builder.register('insertMenu', {
    name: 'Catawiki',
    items: components.map((name) => ({ name })),
  });

  // Deprecated component, registerd but won't show in the Builder.io menu
  selectedPartnerComponentRegister();
  categoryOverviewRegister();
  oldDownloadAppsRegister();
  articleComponentRegister();
  buttonRegister();
  dayAuctionComponentRegister();
  exclusiveSelectionHeaderComponentRegister();
  oldLotsRegister();
  howItWorksComponentRegister();
  expertsListRegister();
  testComponentRegister();

  const testComponents: string[] = [
    devComponentRegister(),

    // TODO: Remove this after these become default
    `v2:${heroComponentName}`,
    `v2:${topicListComponentName}`,
    `v3:${contentImageComponentName}`,
    `v2:${highlightComponentName}`,
    `v2:${singleContentComponentName}`,
    `v2:${FAQComponentName}`,
    `v2:${auctionsComponentName}`,
    `v3:${lotsComponentName}`,
    downloadAppsComponentName,
    `v2:${collectionComponentName}`,
    `v2:${anchorLinksComponentName}`,
    `v3:${listExpertsComponentName}`,
  ];

  Builder.register('insertMenu', {
    name: 'Experiment',
    items: testComponents.map((name) => ({ name })),
  });
  span.end();
}
